import { useMemo, useState } from "react";
import { PageShell } from "../../components/service/PageShell";
import { ServiceHero, CtaPrimary, CtaGhost, FeatureGrid, StepsList, CompareTable, FaqBlock, Section, Overline, SectionTitle, Accent, Reveal } from "../../components/service/Blocks";
import { LeadForm } from "../../components/LeadForm";
import { waLink, IMAGES } from "../../data/site";
import { Factory, BadgePercent, Ban, CalendarCheck, Users, Gift } from "lucide-react";

const fmt = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const WA_MSG = "Vim pelo site. Quero reduzir a conta de energia da minha empresa.";

const FAQ = [
  { q: "Minha empresa precisa fazer obra?", a: "Não. A energia continua chegando pela mesma rede. Muda o contrato de compra da energia, não a instalação." },
  { q: "Quanto a empresa economiza?", a: "Até 30% na conta de energia, conforme o consumo, a demanda e a distribuidora. A proposta é feita com as suas contas: você vê a economia estimada antes de decidir. Use o simulador acima para ter uma ideia." },
  { q: "Precisa pagar alguma coisa para migrar?", a: "Não. Não tem taxa de adesão nem investimento: as taxas da câmara do Mercado Livre já estão incluídas na migração. Se a distribuidora pedir manutenção no padrão de entrada, isso é avaliado à parte." },
  { q: "A distribuidora muda?", a: "Não. A distribuidora local continua cuidando da rede, do medidor e da entrega da energia. Muda só de quem a empresa compra a energia." },
  { q: "Vou receber duas faturas?", a: "Sim. Uma da distribuidora, com o uso da rede e as tarifas obrigatórias, e outra da energia comprada no Mercado Livre, já com o desconto." },
  { q: "Quanto tempo leva a migração?", a: "Em média de 4 a 8 meses, dentro das regras da ANEEL e da câmara do Mercado Livre. A empresa continua funcionando normalmente nesse tempo." },
  { q: "Meus funcionários ganham alguma coisa?", a: "Sim. Os colaboradores ganham, sem custo, desconto na conta de luz de casa e acesso ao iGreen Club, com mais de 600 mil ofertas." },
  { q: "E se a empresa for de baixa tensão?", a: "Então o caminho é o desconto na conta de luz por energia solar de usinas parceiras, que também não exige obra." },
];

const CompanySimulator = () => {
  const [bill, setBill] = useState(15000);
  const [years, setYears] = useState(1);
  const monthly = bill * 0.3;
  const total = monthly * 12 * years;
  const pct = ((bill - 5000) / (200000 - 5000)) * 100;
  return (
    <div className="rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 backdrop-blur-md p-7 sm:p-9" data-testid="empresas-simulator">
      <p className="text-sm font-semibold text-white mb-1.5">Valor médio da conta de energia</p>
      <p className="font-heading text-4xl sm:text-5xl font-extrabold text-[#00E676]">{fmt(bill)}</p>
      <input
        type="range" min="5000" max="200000" step="1000" value={bill}
        onChange={(e) => setBill(parseFloat(e.target.value))}
        className="savings-range mt-7" style={{ "--fill": `${pct}%` }}
        data-testid="empresas-simulator-slider" aria-label="Valor médio da conta de energia"
      />
      <div className="flex justify-between text-[11px] font-mono2 text-emerald-100/40 mt-2">
        <span>R$ 5.000</span><span>R$ 200.000+</span>
      </div>
      <div className="mt-6 flex gap-2">
        {[1, 3, 6].map((y) => (
          <button
            key={y}
            data-testid={`empresas-period-${y}`}
            onClick={() => setYears(y)}
            className={`rounded-full text-xs font-bold px-5 py-2.5 border transition-colors duration-300 ${years === y ? "bg-[#00E676] text-[#04120E] border-[#00E676]" : "border-white/15 text-emerald-100/60 hover:bg-white/5"}`}
          >
            {y} {y === 1 ? "ano" : "anos"}
          </button>
        ))}
      </div>
      <div className="mt-7 border-t border-white/10 pt-6">
        <p className="text-sm text-emerald-100/70">
          Economia estimada de até <strong className="text-white">{fmt(total)}</strong> em {years} {years === 1 ? "ano" : "anos"} com a Conexão Livre
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-[#04120E]/70 border border-white/10 p-4">
            <p className="text-xs text-emerald-100/50">Por mês</p>
            <p className="font-heading text-xl font-bold text-white mt-1" data-testid="empresas-monthly">{fmt(monthly)}</p>
          </div>
          <div className="rounded-xl bg-[#00E676]/10 border border-[#00E676]/25 p-4">
            <p className="text-xs text-emerald-100/50">Com 10% de desconto</p>
            <p className="font-heading text-xl font-bold text-[#00E676] mt-1">{fmt(bill * 0.1 * 12 * years)}</p>
          </div>
        </div>
      </div>
      <p className="mt-5 text-[11px] text-emerald-100/40 leading-relaxed">
        Estimativa com o desconto de até 30% sobre o valor da conta, sem reajustes. O percentual da sua empresa sai da análise gratuita das suas faturas.
      </p>
    </div>
  );
};

export default function Empresas() {
  return (
    <PageShell testid="page-empresas">
      <ServiceHero
        testid="empresas-hero"
        overline="Conexão Livre · Mercado Livre de Energia"
        title={["Até 30% de desconto", "na conta de energia da sua empresa."]}
        sub="A Conexão Livre leva a sua empresa para o Mercado Livre de Energia sem investimento: a energia chega pela mesma rede, só que comprada mais barata, com especialistas cuidando de tudo do início ao fim."
        bullets={["Empresas do Grupo A", "Até 30% de economia", "Sem investimento", "Análise da conta grátis"]}
        primary={<CtaPrimary href="#simulador" external={false} testid="empresas-simulate-cta">Simular no Mercado Livre</CtaPrimary>}
        secondary={<CtaGhost href={waLink(WA_MSG)} testid="empresas-whatsapp-cta">Falar no WhatsApp</CtaGhost>}
        image={IMAGES.solarField}
      />

      <Section className="bg-[#061A14]" testid="empresas-numeros">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            ["Até 30%", "de desconto na conta de energia"],
            ["R$ 0", "de investimento ou taxa de adesão"],
            ["1 a 10 anos", "de contrato com o preço definido"],
            ["+600 mil", "ofertas no iGreen Club para a sua equipe"],
          ].map(([v, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div className="rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 p-6 text-center">
                <p className="font-heading text-2xl sm:text-3xl font-extrabold text-[#00E676]">{v}</p>
                <p className="mt-2 text-xs text-emerald-100/55 leading-snug">{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section testid="empresas-simulador" >
        <div id="simulador" className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline>Simulador de economia</Overline>
            <SectionTitle>Quanto a sua empresa pode <Accent>deixar de pagar</Accent>?</SectionTitle>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/55 leading-relaxed">
              Arraste até o valor médio da conta de energia da empresa e escolha o período. A economia aparece na hora.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-emerald-100/55">
              {["Sem investimento", "Desconto garantido em contrato", "Consultoria especializada"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#00E676]" /> {t}</span>
              ))}
            </div>
            <div className="mt-8">
              <CtaPrimary href={waLink(WA_MSG)} testid="empresas-analysis-cta">Quero a análise grátis da minha conta</CtaPrimary>
            </div>
          </Reveal>
          <Reveal delay={0.12}><CompanySimulator /></Reveal>
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="empresas-o-que-e">
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <Overline>O que é</Overline>
            <SectionTitle>O que é o <Accent>Mercado Livre de Energia</Accent></SectionTitle>
            <p className="mt-5 text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              No Mercado Livre, a empresa deixa de comprar energia só da distribuidora e passa a contratar de outros
              fornecedores, com preço negociado. A rede continua a mesma: a distribuidora só cobra pelo uso dela.
            </p>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              Hoje o Mercado Livre é para empresas ligadas em média e alta tensão (o chamado Grupo A), com transformador
              próprio e demanda contratada. Para casas e pequenos negócios em baixa tensão, o caminho é o desconto na conta de luz.
            </p>
          </Reveal>
          <div className="grid gap-5">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-[#00E676]/25 bg-[#0B261E]/60 p-7">
                <h3 className="font-heading font-bold text-white">É para você se</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-emerald-100/60 leading-relaxed list-disc pl-5">
                  <li>A empresa é atendida em média ou alta tensão</li>
                  <li>Tem transformador próprio e demanda contratada</li>
                  <li>Tem conta de energia alta e quer previsibilidade de custo</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-white/10 bg-[#0B261E]/40 p-7">
                <h3 className="font-heading font-bold text-white">Não é para você se</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-emerald-100/50 leading-relaxed list-disc pl-5">
                  <li>A conta é de baixa tensão (residência ou pequeno comércio): veja o desconto na conta de luz</li>
                  <li>A unidade é do tipo B Optante</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-emerald-100/60">
            <strong className="font-heading text-2xl text-[#00E676]">43%</strong> do consumo de energia do Brasil já está no mercado livre.
          </p>
        </Reveal>
      </Section>

      <Section testid="empresas-por-que">
        <Reveal>
          <Overline>Por que a Conexão Livre</Overline>
          <SectionTitle className="max-w-3xl">Sua empresa não precisa continuar pagando <Accent>o preço do mercado cativo</Accent>.</SectionTitle>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="empresas-features"
            items={[
              { icon: Factory, title: "Para empresas do Grupo A", text: "Indústrias, galpões, hospitais, supermercados e grandes comércios atendidos em média ou alta tensão." },
              { icon: BadgePercent, title: "Até 30% de desconto", text: "Desconto garantido em contrato, mês a mês, comparado ao que a empresa pagaria no mercado cativo." },
              { icon: Ban, title: "Sem investimento", text: "Sem obra, sem placa solar e sem taxa de adesão. Muda o contrato de compra da energia, não a instalação." },
              { icon: CalendarCheck, title: "Preço previsível", text: "Contrato de 1 a 10 anos, protegido das bandeiras tarifárias e dos reajustes da tarifa da distribuidora." },
              { icon: Users, title: "Consultoria do início ao fim", text: "Especialistas cuidam da análise, da proposta, da migração e da relação com a câmara do Mercado Livre." },
              { icon: Gift, title: "Benefício para a equipe", text: "Seus colaboradores ganham, sem custo, desconto na conta de luz de casa e acesso ao iGreen Club." },
            ]}
          />
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12">
            <CompareTable
              testid="empresas-compare"
              leftName="Conexão Livre iGreen"
              rightName="Mercado cativo"
              rows={[
                ["De quem a empresa compra", "Escolhe e negocia no Mercado Livre", "Só da distribuidora, pela tarifa dela"],
                ["Desconto na energia", "Até 30%, garantido em contrato", "Nenhum"],
                ["Previsibilidade", "Preço definido por 1 a 10 anos", "Reajuste anual e bandeiras"],
                ["Investimento", "Zero, sem obra e sem adesão", "Não se aplica"],
                ["Burocracia da migração", "Conduzida pelos especialistas", "Não se aplica"],
                ["Benefício para a equipe", "Desconto na conta de casa e iGreen Club", "Não tem"],
              ]}
            />
          </div>
        </Reveal>
      </Section>

      <Section className="bg-[#061A14]" testid="empresas-passos">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Passo a passo</Overline>
            <SectionTitle>Como funciona a <Accent>migração</Accent></SectionTitle>
          </Reveal>
          <StepsList
            testid="empresas-steps"
            steps={[
              { n: "01", title: "Envie a sua conta de energia", text: "Com ela são analisados a demanda contratada, a tensão de atendimento e o histórico de consumo." },
              { n: "02", title: "Análise de viabilidade grátis", text: "Confirmamos se a empresa é do Grupo A e simulamos o preço real da energia para o seu consumo." },
              { n: "03", title: "Você recebe a proposta", text: "Desconto ou preço garantido, prazo e condições, por escrito, antes de qualquer compromisso." },
              { n: "04", title: "Migração conduzida por especialistas", text: "Todo o processo regulatório fica com a equipe da iGreen. Leva em média de 4 a 8 meses." },
              { n: "05", title: "A empresa passa a economizar", text: "Mesma rede e mesmo fornecimento, com a energia mais barata todo mês." },
            ]}
          />
        </div>
      </Section>

      <FaqBlock testid="empresas-faq" title={<>Perguntas sobre <Accent>energia para empresas</Accent></>} items={FAQ} />

      <LeadForm
        overline="Vamos conversar?"
        title={<>Me conta o seu caso e eu te respondo <Accent>no WhatsApp</Accent>.</>}
        defaultInterest="Energia para minha empresa"
      />
    </PageShell>
  );
}
